<template>
  <div class="service-page">
    <!-- Carousel Hero Section -->
    <ServicesItsCarouselComp 
      :slides="normalCarSlides"
      background-image="/resource/portfolio-04.jpg"
      pattern-image="/shapes/shape-06.png"
      :mission-vision="normalCarMissionVision"
    />

    <!-- Automotive Services Section -->
    <FullScreenSliderComp :content="automotiveServicesContent" />

    <!-- Career Growth Section -->
    <ServiceItsCareerGrowthComp
      :career-data="careerGrowthData"
    />

    <!-- Awards & Recognition Section -->
    <FullScreenSmoothSliderComp :content="awardsRecognitionContent" />

    <!-- Call to Action Section -->
    <ServiceItsCallActionComp
      :cta-data="ctaData"
      :decorative-shapes="ctaDecorativeShapes"
    />
  </div>
</template>

<script>
import { onMounted, computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import WOW from 'wowjs'
import ServicesItsCarouselComp from '@/components/ServicesItsCarouselComp.vue'
import FullScreenSliderComp from '@/components/FullScreenSliderComp.vue'
import FullScreenSmoothSliderComp from '@/components/FullScreenSmoothSliderComp.vue'
import ServiceItsCallActionComp from '@/components/ServiceItsCallActionComp.vue'
import ServiceItsCareerGrowthComp from '@/components/ServiceItsCareerGrowthComp.vue'

export default {
  name: 'NormalCarServicesPage',
  components: {
    ServicesItsCarouselComp,
    FullScreenSliderComp,
    FullScreenSmoothSliderComp,
    ServiceItsCallActionComp,
    ServiceItsCareerGrowthComp
  },
  setup() {
    const route = useRoute()
    
    // Insights data for the slider
    const insightsData = [
      {
        image: require('@/assets/resource/portfolio-01.jpg'),
        title: 'Advanced Engine Diagnostics and Repair',
        tags: ['Blog', 'Engine Services', 'Diagnostics']
      },
      {
        image: require('@/assets/resource/portfolio-02.jpg'),
        title: 'Complete Brake System Overhaul Guide',
        tags: ['Blog', 'Brake Services', 'Safety']
      },
      {
        image: require('@/assets/resource/portfolio-03.jpg'),
        title: 'Transmission Service and Maintenance Tips',
        tags: ['Blog', 'Transmission', 'Maintenance']
      },
      {
        image: require('@/assets/resource/portfolio-04.jpg'),
        title: 'Electric Vehicle Battery Health Monitoring',
        tags: ['Blog', 'EV Services', 'Battery Care']
      },
      {
        image: require('@/assets/resource/portfolio-01.jpg'),
        title: 'Winter Car Preparation Checklist',
        tags: ['Blog', 'Seasonal Care', 'Preparation']
      },
      {
        image: require('@/assets/resource/portfolio-02.jpg'),
        title: 'Hybrid Vehicle Maintenance Best Practices',
        tags: ['Blog', 'Hybrid Cars', 'Maintenance']
      },
      {
        image: require('@/assets/resource/portfolio-03.jpg'),
        title: 'Tire Rotation and Alignment Services',
        tags: ['Blog', 'Tire Services', 'Alignment']
      },
      {
        image: require('@/assets/resource/portfolio-04.jpg'),
        title: 'AC System Repair and Refrigerant Service',
        tags: ['Blog', 'AC Services', 'Climate Control']
      }
    ]
    
    // Awards data for the slider
    const awardsData = [
      {
        image: require('@/assets/resource/portfolio-01.jpg'),
        title: 'Best Automotive Service Provider',
        category: 'Excellence in Customer Service',
        date: '2024',
        tags: ['Service Excellence', 'Customer Satisfaction']
      },
      {
        image: require('@/assets/resource/portfolio-02.jpg'),
        title: 'Innovation in EV Services',
        category: 'Electric Vehicle Technology',
        date: '2024',
        tags: ['Innovation', 'EV Technology']
      },
      {
        image: require('@/assets/resource/portfolio-03.jpg'),
        title: 'Outstanding Brake System Services',
        category: 'Safety & Performance',
        date: '2023',
        tags: ['Safety', 'Performance']
      },
      {
        image: require('@/assets/resource/portfolio-04.jpg'),
        title: 'Best Transmission Repair Center',
        category: 'Technical Excellence',
        date: '2023',
        tags: ['Technical Excellence', 'Repair']
      },
      {
        image: require('@/assets/resource/portfolio-01.jpg'),
        title: 'Customer Choice Award',
        category: 'Public Recognition',
        date: '2023',
        tags: ['Customer Choice', 'Recognition']
      },
      {
        image: require('@/assets/resource/portfolio-02.jpg'),
        title: 'Green Initiative Excellence',
        category: 'Environmental Responsibility',
        date: '2022',
        tags: ['Green Initiative', 'Sustainability']
      }
    ]
    
    // Content objects for slider components
    const automotiveServicesContent = ref({
      header: {
        tag: 'OUR BEST SERVICES',
        title: 'Automotive Services',
        description: 'Comprehensive automotive solutions for all your vehicle maintenance and repair needs'
      },
      items: insightsData
    })
    
    const awardsRecognitionContent = ref({
      header: {
        tag: 'OUR ACHIEVEMENTS',
        title: 'Awards & Recognition',
        description: 'Celebrating our excellence in automotive innovation and service delivery'
      },
      items: awardsData
    })
    
    // Dynamic content based on route
    const serviceData = computed(() => {
      const routeName = route.name
      
      switch(routeName) {
        case 'NormalCarServices':
          return {
            title: 'Normal Car Services',
            description: 'Comprehensive automotive services for traditional gasoline and diesel vehicles. From routine maintenance to major repairs, we keep your car running smoothly and efficiently.',
            services: [
              {
                icon: 'icon-01',
                title: 'Oil Change & Filter',
                description: 'Regular oil changes and filter replacements to maintain engine performance and longevity.',
                features: ['Conventional Oil Change', 'Synthetic Oil Change', 'Oil Filter Replacement', 'Fluid Level Check']
              },
              {
                icon: 'icon-02',
                title: 'Brake Service',
                description: 'Complete brake system inspection, maintenance, and repair services for your safety.',
                features: ['Brake Pad Replacement', 'Brake Disc Resurfacing', 'Brake Fluid Flush', 'Brake System Inspection']
              },
              {
                icon: 'icon-03',
                title: 'Engine Diagnostics',
                description: 'Advanced diagnostic services to identify and resolve engine performance issues.',
                features: ['Computer Diagnostics', 'Engine Tune-up', 'Spark Plug Replacement', 'Fuel System Cleaning']
              },
              {
                icon: 'icon-04',
                title: 'Tire Services',
                description: 'Complete tire care including rotation, balancing, alignment, and replacement services.',
                features: ['Tire Rotation', 'Wheel Balancing', 'Alignment Service', 'Tire Replacement']
              },
              {
                icon: 'icon-05',
                title: 'Transmission Service',
                description: 'Professional transmission maintenance and repair services for smooth gear operation.',
                features: ['Transmission Fluid Change', 'Transmission Filter', 'Gear Adjustment', 'Transmission Repair']
              },
              {
                icon: 'icon-01',
                title: 'AC & Heating',
                description: 'Climate control system maintenance and repair for comfortable driving in all seasons.',
                features: ['AC System Check', 'Refrigerant Recharge', 'Heater Core Service', 'Climate Control Repair']
              }
            ],
            ctaTitle: 'Ready to Service Your Vehicle?',
            ctaDescription: 'Book an appointment today and let our expert technicians take care of your car with professional service and genuine parts.',
            ctaButton: 'Book Service'
          }
        case 'ElectricCarServices':
          return {
            title: 'Electric Car Services',
            description: 'Specialized services for electric vehicles including battery maintenance, charging system diagnostics, and EV-specific repairs. Expert care for your electric vehicle.',
            services: [
              {
                icon: 'icon-01',
                title: 'Battery Service',
                description: 'Electric vehicle battery maintenance, diagnostics, and replacement services.',
                features: ['Battery Health Check', 'Cell Balancing', 'Thermal Management', 'Battery Replacement']
              },
              {
                icon: 'icon-02',
                title: 'Charging System',
                description: 'EV charging infrastructure and onboard charger maintenance and repair.',
                features: ['Charger Diagnostics', 'Port Maintenance', 'Cable Replacement', 'Fast Charging Support']
              },
              {
                icon: 'icon-03',
                title: 'Electric Motor',
                description: 'Electric motor maintenance and repair services for optimal performance.',
                features: ['Motor Diagnostics', 'Bearing Replacement', 'Winding Repair', 'Performance Tuning']
              },
              {
                icon: 'icon-04',
                title: 'Regenerative Braking',
                description: 'Specialized brake system maintenance for electric vehicle regenerative braking.',
                features: ['Brake Pad Service', 'Regen System Check', 'Brake Fluid Service', 'System Calibration']
              },
              {
                icon: 'icon-05',
                title: 'Software Updates',
                description: 'EV software updates and system programming for optimal performance.',
                features: ['Firmware Updates', 'BMS Programming', 'Feature Activation', 'Performance Optimization']
              },
              {
                icon: 'icon-01',
                title: 'Climate Control',
                description: 'Electric vehicle climate control system maintenance and repair.',
                features: ['Heat Pump Service', 'HVAC Diagnostics', 'Cabin Filter', 'Temperature Control']
              }
            ],
            ctaTitle: 'Need Electric Vehicle Service?',
            ctaDescription: 'Trust our EV specialists to maintain your electric vehicle with cutting-edge technology and expert knowledge.',
            ctaButton: 'Schedule EV Service'
          }
        case 'NormalBikeServices':
          return {
            title: 'Normal Bike Services',
            description: 'Complete motorcycle and bicycle maintenance services. From routine tune-ups to major repairs, we keep your two-wheeler running smoothly and safely.',
            services: [
              {
                icon: 'icon-01',
                title: 'Engine Service',
                description: 'Motorcycle engine maintenance and repair services for optimal performance.',
                features: ['Oil Change', 'Spark Plug Service', 'Valve Adjustment', 'Engine Tune-up']
              },
              {
                icon: 'icon-02',
                title: 'Brake System',
                description: 'Complete brake system service for motorcycle safety and performance.',
                features: ['Brake Pad Replacement', 'Brake Fluid Service', 'Disc Resurfacing', 'Cable Adjustment']
              },
              {
                icon: 'icon-03',
                title: 'Chain & Sprockets',
                description: 'Chain maintenance and sprocket replacement for smooth power transmission.',
                features: ['Chain Cleaning', 'Chain Lubrication', 'Sprocket Replacement', 'Chain Tension']
              },
              {
                icon: 'icon-04',
                title: 'Tire Service',
                description: 'Motorcycle tire service including mounting, balancing, and replacement.',
                features: ['Tire Mounting', 'Wheel Balancing', 'Tire Pressure Check', 'Tread Inspection']
              },
              {
                icon: 'icon-05',
                title: 'Electrical System',
                description: 'Motorcycle electrical system diagnostics and repair services.',
                features: ['Battery Service', 'Wiring Repair', 'Lighting System', 'Charging System']
              },
              {
                icon: 'icon-01',
                title: 'Suspension',
                description: 'Motorcycle suspension maintenance and tuning for optimal ride quality.',
                features: ['Shock Service', 'Fork Maintenance', 'Spring Replacement', 'Suspension Tuning']
              }
            ],
            ctaTitle: 'Ready to Service Your Bike?',
            ctaDescription: 'Let our motorcycle experts keep your bike in peak condition with professional service and genuine parts.',
            ctaButton: 'Book Bike Service'
          }
        case 'ElectricBikeServices':
          return {
            title: 'Electric Bike Services',
            description: 'Specialized services for electric bicycles including battery maintenance, motor service, and e-bike specific repairs. Expert care for your electric bicycle.',
            services: [
              {
                icon: 'icon-01',
                title: 'Battery Service',
                description: 'E-bike battery maintenance, charging optimization, and replacement services.',
                features: ['Battery Health Check', 'Charging Optimization', 'Cell Replacement', 'Range Testing']
              },
              {
                icon: 'icon-02',
                title: 'Motor Service',
                description: 'Electric bike motor maintenance and repair for smooth power delivery.',
                features: ['Motor Diagnostics', 'Bearing Service', 'Wiring Check', 'Performance Tuning']
              },
              {
                icon: 'icon-03',
                title: 'Display & Controls',
                description: 'E-bike display and control system maintenance and repair.',
                features: ['Display Service', 'Button Repair', 'Wiring Check', 'System Updates']
              },
              {
                icon: 'icon-04',
                title: 'Charging System',
                description: 'E-bike charging system maintenance and repair services.',
                features: ['Charger Service', 'Port Maintenance', 'Cable Replacement', 'Charging Diagnostics']
              },
              {
                icon: 'icon-05',
                title: 'Brake System',
                description: 'E-bike brake system service including regenerative braking maintenance.',
                features: ['Brake Pad Service', 'Disc Maintenance', 'Cable Service', 'Regen System']
              },
              {
                icon: 'icon-01',
                title: 'General Maintenance',
                description: 'Complete e-bike maintenance including drivetrain and frame service.',
                features: ['Chain Service', 'Gear Adjustment', 'Frame Check', 'Tire Service']
              }
            ],
            ctaTitle: 'Need E-Bike Service?',
            ctaDescription: 'Trust our e-bike specialists to maintain your electric bicycle with expert knowledge and specialized tools.',
            ctaButton: 'Schedule E-Bike Service'
          }
        default:
          return {
            title: 'Normal Car Services',
            description: 'Comprehensive automotive services for traditional gasoline and diesel vehicles.',
            services: [],
            ctaTitle: 'Ready to Service Your Vehicle?',
            ctaDescription: 'Book an appointment today.',
            ctaButton: 'Book Service'
          }
      }
    })
    

    onMounted(async () => {
      // Initialize WOW.js
      new WOW.WOW({
        mobile: false
      }).init()

      // Import and initialize Owl Carousel
      try {
        await import('owl.carousel')
        
        // Initialize Owl Carousel for banner
        setTimeout(() => {
          if (window.$ && window.$.fn.owlCarousel) {
            window.$('.banner-carousel').owlCarousel({
              loop: true,
              margin: 0,
              nav: true,
              active: true,
              smartSpeed: 1000,
              autoplay: 6000,
              navText: ['<span class="icon-05"></span>', '<span class="icon-06"></span>'],
              responsive: {
                0: {
                  items: 1
                },
                600: {
                  items: 1
                },
                768: {
                  items: 1
                },
                992: {
                  items: 1
                },
                1200: {
                  items: 1
                }
              }
            })
          }
        }, 100)
      } catch (error) {
        console.error('Error loading Owl Carousel:', error)
      }

        })
        
        // Normal Car Services slides data
        const normalCarSlides = ref([
          {
            subtitle: 'Car Services',
            titleLines: [
              { text: 'Professional ', highlight: false },
              { text: 'Car ', highlight: 'Maintenance' }
            ],
            description: 'Comprehensive car services for all makes and models. From routine maintenance to complex repairs, we provide professional automotive care solutions.',
            buttonText: 'Get Service',
            buttonLink: '/contact'
          },
          {
            subtitle: 'Expert Technicians',
            titleLines: [
              { text: 'Certified ', highlight: 'Professionals' }
            ],
            description: 'Our certified technicians use advanced diagnostic equipment and genuine parts to ensure your car receives the best possible care and maintenance.',
            buttonText: 'Learn More',
            buttonLink: '/contact'
          },
          {
            subtitle: 'Quality Service',
            titleLines: [
              { text: 'Reliable ', highlight: 'Solutions' }
            ],
            description: 'We offer a comprehensive range of car services including engine repair, brake service, transmission work, and preventive maintenance to keep your vehicle running smoothly.',
            buttonText: 'Book Now',
            buttonLink: '/contact'
          }
        ]);

        // Normal Car Services mission/vision data
        const normalCarMissionVision = ref({
          vision: {
            title: 'Our Vision',
            description: 'To be the leading car service provider offering comprehensive solutions for all vehicle types with unmatched quality and customer satisfaction.'
          },
          mission: {
            title: 'Our Mission',
            description: 'To deliver exceptional car services using advanced technology, skilled technicians, and genuine parts to ensure optimal vehicle performance and safety.'
          }
        });

        // CTA data for the call to action component
        const ctaData = computed(() => {
          const routeName = route.name
          
          switch(routeName) {
            case 'NormalCarServices':
              return {
                title: 'Ready to Service Your Vehicle?',
                description: 'Book an appointment today and let our expert technicians take care of your car with professional service and genuine parts.',
                primaryButton: {
                  text: 'Book Service',
                  link: '/contact',
                  icon: 'icon-02'
                },
                secondaryButton: {
                  text: 'View All Services',
                  link: '/services',
                  icon: 'icon-02'
                }
              }
            case 'ElectricCarServices':
              return {
                title: 'Need Electric Vehicle Service?',
                description: 'Trust our EV specialists to maintain your electric vehicle with cutting-edge technology and expert knowledge.',
                primaryButton: {
                  text: 'Schedule EV Service',
                  link: '/contact',
                  icon: 'icon-02'
                },
                secondaryButton: {
                  text: 'View All Services',
                  link: '/services',
                  icon: 'icon-02'
                }
              }
            case 'NormalBikeServices':
              return {
                title: 'Ready to Service Your Bike?',
                description: 'Let our motorcycle experts keep your bike in peak condition with professional service and genuine parts.',
                primaryButton: {
                  text: 'Book Bike Service',
                  link: '/contact',
                  icon: 'icon-02'
                },
                secondaryButton: {
                  text: 'View All Services',
                  link: '/services',
                  icon: 'icon-02'
                }
              }
            case 'ElectricBikeServices':
              return {
                title: 'Need E-Bike Service?',
                description: 'Trust our e-bike specialists to maintain your electric bicycle with expert knowledge and specialized tools.',
                primaryButton: {
                  text: 'Schedule E-Bike Service',
                  link: '/contact',
                  icon: 'icon-02'
                },
                secondaryButton: {
                  text: 'View All Services',
                  link: '/services',
                  icon: 'icon-02'
                }
              }
            default:
              return {
                title: 'Ready to Service Your Vehicle?',
                description: 'Book an appointment today.',
                primaryButton: {
                  text: 'Book Service',
                  link: '/contact',
                  icon: 'icon-02'
                },
                secondaryButton: {
                  text: 'View All Services',
                  link: '/services',
                  icon: 'icon-02'
                }
              }
          }
        })

        // Decorative shapes for CTA component
        const ctaDecorativeShapes = {
          shape1: '/shapes/shape-07.png',
          shape2: '/shapes/shape-08.png',
          shape3: '/shapes/shape-09.png',
          shape4: '/shapes/shape-10.png'
        }

        // Career growth data
        const careerGrowthData = {
          title: "Grow your career with us",
          description: "Unlock endless opportunities for innovation, development, and professional growth in a dynamic, forward-thinking environment.",
          buttonText: "Find your next job",
          buttonLink: "/careers"
        }

        return {
          serviceData,
          insightsData,
          awardsData,
          normalCarSlides,
          normalCarMissionVision,
          automotiveServicesContent,
          awardsRecognitionContent,
          ctaData,
          ctaDecorativeShapes,
          careerGrowthData
        }
  }
}
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Custom button styling for carousel - More specific to avoid conflicts */
.service-page .banner__two .btn-box .theme-btn {
  padding: 12px 30px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
  border-radius: 0 !important;
  background: linear-gradient(219deg, #FB0E76 0%, #910694 100%) !important;
  color: #fff !important;
  text-decoration: none !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 8px !important;
  transition: all 0.3s ease !important;
}

.service-page .banner__two .btn-box .theme-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 5px 15px rgba(251, 14, 118, 0.4) !important;
  color: #fff !important;
  text-decoration: none !important;
}

.service-page .banner__two .btn-box .theme-btn i {
  font-size: 12px !important;
  transition: transform 0.3s ease !important;
}

.service-page .banner__two .btn-box .theme-btn:hover i {
  transform: translateX(3px) !important;
}

/* Mission/Vision block styling */
.banner__mission__block {
  border-radius: 10px 0px 0px 0px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(15px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  margin-top: 20px;
}

.mission__block {
  padding: 40px 50px;
  border-right: 1px solid rgba(255, 255, 255, 0.10);
  flex: 1;
}

.mission__block:last-child {
  border-right: none;
}

.mission__block h3 {
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.mission__block p {
  color: #ccc;
  font-size: 14px;
  line-height: 24px;
  margin: 0;
  font-family: 'Open Sans', sans-serif;
}

/* Reduce banner height - More specific to avoid conflicts */
.service-page section.banner__two {
  padding-top: 100px !important;
  padding-bottom: 100px !important;
}

.service-page .banner__two .content-box {
  padding-bottom: 60px !important;
}

/* Responsive adjustments - More specific to avoid conflicts */
@media (max-width: 768px) {
  .service-page .banner__two .content-box h2 {
    font-size: 50px !important;
    line-height: 60px !important;
  }
  
  .service-page .mission__block {
    padding: 30px 25px !important;
  }
  
  .service-page .mission__block h3 {
    font-size: 18px !important;
  }
  
  .service-page .mission__block p {
    font-size: 13px !important;
    line-height: 22px !important;
  }
}

/* Career Growth Section - Now handled by ServiceItsCareerGrowthComp component */


/* Responsive Design for Career Section - Now handled by ServiceItsCareerGrowthComp component */

/* Awards & Recognition Section */
.awards-recognition-section {
  padding: 100px 0;
  background: linear-gradient(135deg, #0a0f1c 0%, #1a2332 50%, #0a0f1c 100%);
  color: #fff;
  position: relative;
  overflow: hidden;
}

.awards-recognition-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, 
    rgba(255, 215, 0, 0.05) 0%, 
    rgba(255, 165, 0, 0.03) 25%, 
    rgba(255, 140, 0, 0.05) 50%, 
    rgba(255, 69, 0, 0.03) 75%, 
    rgba(255, 215, 0, 0.05) 100%);
  pointer-events: none;
}

.awards-slider {
  width: 100%;
  padding: 0 20px;
  position: relative;
  z-index: 2;
}

.award-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 15px;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  margin: 0 10px;
}

.award-card:hover {
  transform: translateY(-5px);
  border-color: rgba(255, 215, 0, 0.4);
  box-shadow: 0 15px 40px rgba(255, 215, 0, 0.1);
}

.award-card:hover .award-overlay {
  opacity: 1;
}

.award-image-container {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.award-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.award-card:hover .award-image {
  transform: scale(1.05);
}

.award-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, 
    rgba(0, 0, 0, 0.3) 0%, 
    rgba(0, 0, 0, 0.1) 50%, 
    rgba(0, 0, 0, 0.3) 100%);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 15px;
  opacity: 0;
  transition: all 0.3s ease;
}

.award-badge {
  background: linear-gradient(135deg, #ffd700 0%, #ff8c00 100%);
  color: #000;
  padding: 8px 12px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
  align-self: flex-end;
  margin-bottom: 10px;
}

.award-badge i {
  font-size: 10px;
}

.award-content {
  padding: 0;
  margin-top: auto;
  width: 100%;
}

.award-title {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 8px 0;
  line-height: 1.3;
  font-family: 'Archivo', sans-serif;
}

.award-category {
  font-size: 14px;
  color: #ffd700;
  margin: 0 0 5px 0;
  font-weight: 600;
}

.award-date {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 15px 0;
}

.award-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.award-tag {
  background: rgba(255, 215, 0, 0.1);
  color: #ffd700;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(255, 215, 0, 0.2);
}

/* Responsive Design for Awards Section */
@media (max-width: 991px) {
  .awards-recognition-section {
    padding: 80px 0;
  }
  
  .awards-slider {
    padding: 0 10px;
  }
  
  .award-card {
    margin: 0 5px;
  }
  
  .award-image-container {
    height: 180px;
  }
  
  .award-title {
    font-size: 16px;
  }
}

@media (max-width: 767px) {
  .award-image-container {
    height: 160px;
  }
  
  .award-title {
    font-size: 14px;
  }
  
  .award-category {
    font-size: 12px;
  }
  
  .award-content {
    padding: 15px;
  }
}

/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';

/* Latest Insights Section */
.latest-insights-section {
  padding: 100px 0;
  background: #00061A;
  color: #fff;
}

.latest-insights-section .container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}

.insights-slider {
  width: 100%;
  padding: 0 20px;
}

.section-header {
  text-align: center;
  margin-bottom: 80px;
  position: relative;
}

.section-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(246, 236, 79, 0.1);
  border: 1px solid rgba(246, 236, 79, 0.3);
  padding: 8px 16px;
  border-radius: 25px;
  margin-bottom: 20px;
  font-size: 12px;
  font-weight: 600;
  color: #F6EC4F;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.section-tag i {
  font-size: 10px;
}

.section-header h2 {
  font-size: 48px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 15px 0;
  font-family: 'Archivo', sans-serif;
}

.section-description {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 30px 0;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.header-controls {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 20px;
}

.navigation-arrows {
  display: flex;
  gap: 10px;
}

.arrow-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.arrow-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.4);
  transform: scale(1.1);
}

.see-all-link {
  color: #fff;
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.see-all-link:hover {
  color: #F6EC4F;
  gap: 12px;
}

.see-all-link i {
  font-size: 12px;
  transition: transform 0.3s ease;
}

.see-all-link:hover i {
  transform: translateX(3px);
}

.insights-slider {
  position: relative;
  overflow: hidden;
}

.insight-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(246, 236, 79, 0.1);
  border-radius: 15px;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  margin: 0 10px;
}

.insight-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  border-color: rgba(246, 236, 79, 0.3);
}

.insight-image-container {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.insight-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.insight-card:hover .insight-image {
  transform: scale(1.05);
}

.category-tags {
  position: absolute;
  top: 15px;
  left: 15px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.category-tag {
  background: rgba(139, 69, 19, 0.9);
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.insight-content {
  padding: 20px;
}

.insight-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  margin: 0;
  line-height: 1.4;
  font-family: 'Open Sans', sans-serif;
}

/* Responsive Design */
@media (max-width: 991px) {
  .latest-insights-section {
    padding: 80px 0;
  }
  
  .section-header h2 {
    font-size: 36px;
  }
  
  .section-description {
    font-size: 16px;
    max-width: 500px;
  }
  
  .header-controls {
    position: static;
    justify-content: center;
    margin-top: 20px;
    flex-direction: row;
    gap: 15px;
  }
}

@media (max-width: 767px) {
  .section-header h2 {
    font-size: 28px;
  }
  
  .section-description {
    font-size: 14px;
    max-width: 100%;
    padding: 0 20px;
  }
  
  .header-controls {
    flex-direction: column;
    gap: 15px;
  }
  
  .section-tag {
    font-size: 11px;
    padding: 6px 12px;
  }
  
  .insight-image-container {
    height: 180px;
  }
  
  .insight-content {
    padding: 15px;
  }
  
  .insight-title {
    font-size: 14px;
  }
}

/* Global Styles */
body {
  font-family: 'Open Sans', sans-serif;
  line-height: 1.6;
  color: #333;
}

.see__pad {
  padding: 100px 0;
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}

/* Pattern Layers */
.pattern-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.pattern-1 {
  top: 10%;
  right: 5%;
  width: 100px;
  height: 100px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-2 {
  bottom: 20%;
  left: 8%;
  width: 80px;
  height: 80px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-3 {
  top: 30%;
  left: 10%;
  width: 120px;
  height: 120px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-4 {
  top: 15%;
  right: 15%;
  width: 90px;
  height: 90px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-5 {
  bottom: 25%;
  right: 20%;
  width: 70px;
  height: 70px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-6 {
  top: 40%;
  left: 5%;
  width: 110px;
  height: 110px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.09;
}

.pattern-7 {
  bottom: 15%;
  left: 12%;
  width: 85px;
  height: 85px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-8 {
  top: 35%;
  right: 8%;
  width: 95px;
  height: 95px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

/* Intro Section */
.service-intro {
  background: #00061A;
  position: relative;
  overflow: hidden;
}

.service-intro-content {
  position: relative;
  z-index: 2;
}

.service-section-label h4 {
  font-size: 20px;
  font-weight: 400;
  letter-spacing: 13.2px;
  color: #F6EC4F;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
  text-transform: uppercase;
}

.service-main-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.service-description p {
  font-size: 18px;
  color: #fff;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.8;
}

/* Services Offered Section */
.services-offered {
  background: #00061A;
  position: relative;
  overflow: hidden;
}

.services-header {
  position: relative;
  z-index: 2;
  margin-bottom: 60px;
}

.services-section-label h4 {
  font-size: 20px;
  font-weight: 400;
  letter-spacing: 13.2px;
  color: #F6EC4F;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
  text-transform: uppercase;
}

.services-main-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.services-description p {
  font-size: 18px;
  color: #fff;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.8;
}

.service-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(246, 236, 79, 0.1);
  border-radius: 15px;
  padding: 40px 30px;
  height: 100%;
  position: relative;
  transition: all 0.3s ease;
  overflow: hidden;
}

.service-item:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 50px rgba(246, 236, 79, 0.2);
  border-color: #F6EC4F;
}

.service-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.block-shape-1 {
  position: absolute;
  top: -20px;
  right: -20px;
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, rgba(246, 236, 79, 0.1), rgba(246, 236, 79, 0.05));
  border-radius: 50%;
  opacity: 0.3;
}

.block-shape-2 {
  position: absolute;
  bottom: -30px;
  left: -30px;
  width: 80px;
  height: 80px;
  background: linear-gradient(45deg, rgba(246, 236, 79, 0.08), rgba(246, 236, 79, 0.03));
  border-radius: 50%;
  opacity: 0.4;
}

.service-content {
  position: relative;
  z-index: 2;
}

.service-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #F6EC4F, #E6D43A);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
}

.service-icon i {
  font-size: 24px;
  color: #00061A;
}

.service-content h3 {
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.service-content p {
  font-size: 16px;
  color: #fff;
  margin-bottom: 20px;
  line-height: 1.6;
}

.service-content ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.service-content ul li {
  color: #fff;
  font-size: 14px;
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
}

.service-content ul li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #F6EC4F;
  font-weight: bold;
}

/* Call to Action Section */
/* Call to Action Section - Now handled by ServiceItsCallActionComp component */

/* Responsive Design */
@media (max-width: 767px) {
  .service-main-title h2,
  .services-main-title h2 {
    font-size: 36px;
    line-height: 46px;
  }
  
  .service-section-label h4,
  .services-section-label h4 {
    letter-spacing: 8px;
    font-size: 16px;
  }
  
  .service-item {
    padding: 30px 20px;
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .cta-btn {
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
}

@media (max-width: 399px) {
  .see__pad {
    padding: 60px 0;
  }
  
  .service-main-title h2,
  .services-main-title h2,
  .cta-title h2 {
    font-size: 28px;
    line-height: 38px;
  }
}
</style> 